import Vue from 'vue';
import documentService from '@/api/document-service';
import eventBus, { OPEN_SNACKBAR, openConfirmDialog } from '@/util/event-bus';
import i18n from '@/i18n/i18n-config';
import { removeArrayItem, updateArrayItem } from '@/util/array';
import { mapErrorsToInputs } from '@/util/forms';
import subjectService from '@/api/subject-service';
import permitService from '@/api/permit-service';

const getDefaultDocumentFormItem = () => ({
  files: [],
  is_visible: true,
});

export const getDefaultDocumentFilterParams = () => ({});

const state = {
  documents: null,
  documentPagination: {},
  editedDocument: {},
  documentValidationErrors: {},
  documentFilterParams: getDefaultDocumentFilterParams(),
  newDocument: getDefaultDocumentFormItem(),
  selectedDocument: {},
};

const getters = {
  documentTypes() {
    return [
      {
        name: i18n.t('id_card'),
        value: 'id_card',
      },
      {
        name: i18n.t('informatics_and_communications_department_notice'),
        value: 'informatics_and_communications_department_notice',
      },
      {
        name: i18n.t('lithuanian_transport_safety_notice'),
        value: 'lithuanian_transport_safety_notice',
      },
      {
        name: i18n.t('studies_notice'),
        value: 'studies_notice',
      },
      {
        name: i18n.t('car_registration'),
        value: 'car_registration',
      },
      {
        name: i18n.t('technical_inspection_document'),
        value: 'technical_inspection_document',
      },
      {
        name: i18n.t('parking_contract_with_airports'),
        value: 'parking_contract_with_airports',
      },
      {
        name: i18n.t('parking_scheme'),
        value: 'parking_scheme',
      },
      {
        name: i18n.t('clean_criminal_record'),
        value: 'clean_criminal_record',
      },
      {
        name: i18n.t('impeccable_reputation'),
        value: 'impeccable_reputation',
      },
      {
        name: i18n.t('other'),
        value: 'other',
      },
    ];
  },

  modelTypesMap() {
    return {
      'App\\Models\\Subject': {
        label: i18n.t('subject'),
        value: 'App\\Models\\Subject',
        itemText: 'title',
        searchFunction: subjectService.search,
      },
      'App\\Models\\Permit': {
        label: i18n.t('permit'),
        value: 'App\\Models\\Permit',
        itemText: 'permit_number',
        searchFunction: permitService.search,
      },
    };
  },
};

const mutations = {
  SET_DOCUMENTS(state, { data, current_page, per_page, total }) {
    state.documents = data;
    state.documentPagination = {
      current_page,
      per_page,
      total,
    };
  },

  SET_DOCUMENT_FILTER_PARAMS(state, params) {
    state.documentFilterParams = params;
  },

  SET_EDITED_DOCUMENT(state, document) {
    state.documentValidationErrors = {};
    state.editedDocument = JSON.parse(JSON.stringify(document));
  },

  STORE_DOCUMENT(state, doc) {
    state.documents = [...(state.documents || []), doc];
    state.documentPagination.total += doc.length;
    state.documentValidationErrors = {};
    state.newDocument = getDefaultDocumentFormItem();
  },

  UPDATE_DOCUMENT(state, document) {
    state.documents = updateArrayItem(state.documents, document);
  },

  UPDATE_DOCUMENT_DATES(state, document) {
    for (let i = 0; i < state.documents.length; i++) {
      if (state.documents[i].id === document.document_id) {
        state.documents[i].start_at = document.start_at;
        state.documents[i].end_at = document.end_at;
      }
    }
  },

  DELETE_DOCUMENT(state, document) {
    state.documents = removeArrayItem(state.documents || [], document);
    state.documentPagination.total -= 1;
  },

  SET_DOCUMENT_VALIDATION_ERRORS(state, documentValidationErrors) {
    state.documentValidationErrors = documentValidationErrors;
  },

  RESET_DOCUMENT_VALIDATION_ERRORS(state) {
    state.documentValidationErrors = {};
  },

  CLEAR_DOCUMENT_VALIDATION_ERRORS(state, field) {
    Vue.delete(state.documentValidationErrors, field);
  },

  SET_SELECTED_DOCUMENT(state, selectedDocument) {
    state.selectedDocument = selectedDocument;
  },
};

const actions = {
  fetchDocuments({ commit }, params) {
    commit('SET_DOCUMENT_FILTER_PARAMS', params);
    return documentService.getPage(params).then((res) => {
      commit('SET_DOCUMENTS', res.data);
      if (params.page !== state.documentFilterParams.page) {
        window.scrollTo(0, 0);
      }
      return res.data;
    });
  },

  storeDocument({ commit }, document) {
    return documentService
      .create(document)
      .then((res) => {
        commit('STORE_DOCUMENT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('document_created'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_DOCUMENT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  editDocument({ state, commit }, documentId) {
    const document = state.documents?.find((d) => d.id === documentId);
    if (document) {
      commit('SET_EDITED_DOCUMENT', document);
      return Promise.resolve(document);
    }
    return documentService.getById(documentId).then((res) => {
      commit('SET_EDITED_DOCUMENT', res.data);
      return res.data;
    });
  },

  updateDocument({ commit }, document) {
    return documentService
      .update(document)
      .then((res) => {
        commit('UPDATE_DOCUMENT', res.data);
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('document_updated'));
        return res.data;
      })
      .catch((err) => {
        commit('SET_DOCUMENT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  clearDocumentValidationErrors({ commit }, field) {
    commit('CLEAR_DOCUMENT_VALIDATION_ERRORS', field);
  },

  copyPermitDocument({ commit }, documentToCopy) {
    return documentService
      .copyPermitDocument(documentToCopy)
      .then((res) => {
        commit('permits/SET_EDITED_PERMIT', res.data.data, { root: true });
        commit('permits/UPDATE_PERMIT', res.data.data, { root: true });
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('document_copied'));
      })
      .catch((err) => {
        commit('SET_DOCUMENT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  updateDocumentDates({ commit }, { document, previewLink }) {
    return documentService
      .updateDocumentDates(document)
      .then((res) => {
        if (previewLink === 'document/view') {
          commit('UPDATE_DOCUMENT_DATES', document);
        } else {
          commit('permits/SET_EDITED_PERMIT', res.data.data, { root: true });
          commit('permits/UPDATE_PERMIT', res.data.data, { root: true });
        }
        eventBus.$emit(OPEN_SNACKBAR, i18n.t('document_dates_updated'));
      })
      .catch((err) => {
        commit('SET_DOCUMENT_VALIDATION_ERRORS', mapErrorsToInputs(err));
        throw err;
      });
  },

  async deleteDocument({ commit }, document) {
    const wasConfirmed = await openConfirmDialog({
      title: i18n.t('confirm_document_delete'),
    });
    if (!wasConfirmed) {
      return false;
    }
    await documentService.delete(document);
    commit('DELETE_DOCUMENT', document);
    eventBus.$emit(OPEN_SNACKBAR, i18n.t('document_deleted'));
    return true;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
